import { yupResolver } from "@hookform/resolvers/yup";
import isEmpty from "lodash/isEmpty";
import Link from "next/link";
import { useEffect, useState, useCallback } from "react";
import { Controller, useForm } from "react-hook-form";
import * as y from "yup";

import { Button } from "@/components/Buttons";
import {
  ReadOnlyField,
  SelectField,
  TextField,
  TextAreaField,
  HiddenField,
  SingleCheckboxField,
} from "@/components/Form/Parts/index";
import config from "@/lib/config";
import { inquiryList } from "@/lib/constants";
import yupInquiryNameValidate from "@/lib/yupInquiryNameValidate";
import formStyles from "@/styles/Form.module.scss";
import processStyles from "@/styles/Process.module.scss";

function createOption(value, format) {
  return { value: value, label: format };
}

function inquiryOptions() {
  const options = [];
  for (const key in inquiryList) {
    options.push(createOption(key, inquiryList[key]));
  }
  return options;
}

yupInquiryNameValidate(y);

export default function Step1({ onSubmit, user, formData, privacypolicy }) {
  const mailAddressField = y
    .string()
    .email("メールアドレスの形式で入力してください。")
    .required("メールアドレスが入力されていません。");

  const pastSpooxIdField = y
    .string()
    .email("メールアドレスの形式で入力してください。");

  const commentOptionField = y
    .string()
    .required("お問い合わせ内容を選択してください。");

  const commentField = y
    .string()
    .max(2000, "2000文字以内で入力してください。")
    .required("お問い合わせ詳細を入力してください。");

  let schema;
  if (user) {
    schema = y.object().shape({
      mailAddress: mailAddressField,
      pastSpooxId: pastSpooxIdField,
      commentOption: commentOptionField,
      comment: commentField,
    });
  } else {
    schema = y.object().shape({
      fullName: y
        .string()
        .required("お名前を入力してください。")
        .matches(
          /^([^\x01-\x7E\uFF65-\uFF9F]| )+$/,
          "お名前は全角文字で入力してください。"
        )
        .max(20, "20文字以内で入力してください。")
        .inquiryName(),
      mailAddress: mailAddressField,
      membersId: y.string().email("メールアドレスの形式で入力してください。"),
      pastSpooxId: pastSpooxIdField,
      commentOption: commentOptionField,
      comment: commentField,
      privacypolicy: y.boolean().oneOf([true], "規約に同意してください。"),
    });
  }

  const { formState, register, handleSubmit, setValue, trigger, control } =
    useForm({
      resolver: yupResolver(schema),
      mode: "all",
    });

  useEffect(() => {
    if (!isEmpty(formData)) {
      setValue(
        "commentOption",
        typeof formData.commentOption === "string"
          ? formData.commentOption
          : formData?.commentOption?.value || "",
        {
          shouldValidate: true,
          shouldDirty: true,
        }
      );
      setValue("fullName", formData?.fullName || user?.fullName, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue("mailAddress", formData?.mailAddress || user?.mailAddress, {
        shouldValidate: true,
        shouldDirty: true,
      });

      setValue("comment", formData?.comment, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [formData, user]);
  const { isDirty, isValid, isSubmitting, errors } = formState;
  const [commentOption, setCommentOption] = useState(
    formData?.commentOption || ""
  );
  const isLoggedIn = Boolean(user);
  const handleCommentOptionChange = useCallback((e) => {
    setCommentOption(e.value);
  }, []);

  useEffect(() => {
    if (commentOption) {
      trigger("commentOption");
    }
  }, [commentOption]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className={processStyles.contentWrapper}>
        <div className={processStyles.content}>
          <p>
            ※お問い合わせの前に：よくあるご質問は
            <Link
              href={config.staticLinks.help}
              target="_blank"
              rel="noopener noreferrer"
              className={formStyles.textLink}
            >
              こちら
            </Link>
            からご確認いただけます。
          </p>
          <br />
          <p className={`${processStyles.mailForm}`}>
            お問い合わせの内容によっては、回答にお時間を要する場合がありますので、あらかじめご了承ください。
            <br />
            スカパー！メンバーズIDにてスカパー！動画ストア利用登録済みの方は、ログインしたうえでお問い合わせください。
            <br />
            ご登録状況確認のため、
            <span className={processStyles.bold}>
              「スカパー！メンバーズID」と「過去ご登録の旧SPOOX ID」
              のどちらかは必ずご入力の上、
            </span>
            お問い合わせください。
            <br />
            <span className={processStyles.bold}>
              「ご連絡可能なメールアドレス」 に誤りがある場合は返信できません
            </span>
            ので、ご注意のうえご入力ください。
          </p>
          <fieldset className={`${formStyles.basicFieldset}`}>
            <div className={formStyles.inputField}>
              <div className={formStyles.formGroup}>
                {!isLoggedIn && (
                  <TextField
                    {...register("fullName")}
                    title="名前（漢字、又はカナ）"
                    caption="※ご登録済みのお客様は、スカパー！メンバーズ、もしくは、スカパー！動画ストアにご登録のお名前をご入力ください。"
                    placeholder="お名前"
                    error={errors.fullName?.message}
                    required
                  />
                )}
                {isLoggedIn && (
                  <ReadOnlyField
                    title="名前"
                    name="fullName"
                    value={user?.fullName}
                    required
                  />
                )}
              </div>
            </div>
          </fieldset>
          <fieldset className={formStyles.basicFieldset}>
            <TextField
              {...register("mailAddress")}
              title="ご連絡可能なメールアドレス"
              placeholder="ご連絡可能なメールアドレス"
              error={errors.mailAddress?.message}
              required
            />
          </fieldset>
          <fieldset className={formStyles.basicFieldset}>
            {!isLoggedIn && (
              <TextField
                {...register("membersId")}
                title="スカパー！メンバーズID（ご登録の方のみ）"
                placeholder="スカパー！メンバーズID"
                error={errors.membersId?.message}
              />
            )}
            {isLoggedIn && (
              <ReadOnlyField
                name="membersId"
                value={user?.mailAddress}
                title="スカパー！メンバーズID（ご登録の方のみ）"
              />
            )}
          </fieldset>
          <fieldset className={formStyles.basicFieldset}>
            <TextField
              {...register("pastSpooxId")}
              title="過去ご登録の旧SPOOX ID（ご登録の方のみ）"
              placeholder="過去ご登録の旧SPOOX ID"
              error={errors.pastSpooxId?.message}
            />
          </fieldset>
          <fieldset className={formStyles.basicFieldset}>
            <Controller
              name="commentOption"
              control={control}
              defaultValue={
                typeof formData?.commentOption === "string"
                  ? formData.commentOption
                  : formData?.commentOption?.value || ""
              }
              rules={{ required: "お問い合わせ内容を選択してください" }}
              render={({ field }) => (
                <SelectField
                  title="お問い合わせ内容"
                  options={inquiryOptions()}
                  placeholder="お問い合わせ内容"
                  value={createOption(field.value, inquiryList[field.value])}
                  onChange={(option) => {
                    field.onChange(option.value);
                    handleCommentOptionChange(option);
                  }}
                  error={errors.commentOption?.message}
                  required
                />
              )}
            />
          </fieldset>
          <fieldset className={formStyles.basicFieldset}>
            <TextAreaField
              {...register("comment")}
              title="お問い合わせ内容の詳細"
              placeholder="コメント"
              error={errors.comment?.message}
              required
            />
          </fieldset>
        </div>
      </div>
      <div className={formStyles.guideArea}>
        <p>
          使用している端末（パソコン、スマートフォン、タブレット等）や、ご利用のネットワーク環境などの情報を添えて、お問い合わせの内容はできるだけ詳しくお書きください。
        </p>
        <ul>
          {user && privacypolicy && (
            <li>
              送信いただく前に、
              <Link
                href={privacypolicy.url}
                target="_blank"
                rel="noopener noreferrer"
                className={formStyles.textLink}
              >
                プライバシーポリシー
              </Link>
              の内容を必ずお読みください。またお客様本人が同意した上で個人情報をご入力ください。
            </li>
          )}
          <li>
            回答に時間がかかる場合がございます。また、お問い合わせ内容によってはご返信できない場合がございます。あらかじめご了承ください。
          </li>
          <li>
            日本語以外のお問い合わせにはお応えできませんので、ご了承ください。
            <br />
            Please understand that all information is provided in Japanese only.
          </li>
        </ul>
        {!user && privacypolicy && (
          <fieldset className={formStyles.basicFieldset}>
            <SingleCheckboxField
              {...register("privacypolicy")}
              title="プライバシーポリシー"
              defaultChecked={false}
              label={
                <p className={formStyles.terms}>
                  <Link
                    href={privacypolicy.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {privacypolicy.display_name}
                  </Link>
                  を確認の上、同意する
                </p>
              }
              error={errors.privacypolicy?.message}
              required
            />
          </fieldset>
        )}
      </div>
      <div className={formStyles.btnArea}>
        <Button
          type="submit"
          fillColor
          large
          fontFixed
          disabled={!isDirty || !isValid}
          loading={isSubmitting}
        >
          次へ
        </Button>
      </div>
    </form>
  );
}
